import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileText, Loader2, Eye, EyeOff, Smartphone, ArrowLeft } from 'lucide-react';
import PageNavigation from '@/components/PageNavigation';
import LanguageSelector from '@/components/LanguageSelector';
import { authAPI } from '@/lib/api';
import { toast } from 'sonner';
import SkipNav from '@/components/SkipNav';
import { initializeCsrf } from '@/utils/csrf';
import LinkedInLoginButton from '@/components/LinkedInLoginButton';
import { GoogleLoginButton } from '@/components/GoogleLoginButton';
import { trackEvent, identifyUser, EVENTS } from '@/utils/analytics';

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [emailError, setEmailError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  
  // 2FA states
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!formData.email || !validateEmail(formData.email)) {
      setEmailError('Please enter a valid email address');
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setEmailError('');

    try {
      const loginData = { ...formData, remember_me: rememberMe, subscribe_newsletter: subscribeNewsletter };
      
      // If 2FA is required, include the code
      if (requires2FA && twoFactorCode) {
        loginData.two_factor_code = twoFactorCode;
      }
      
      const response = await authAPI.login(loginData);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        setRequires2FA(true);
        setPendingUserId(response.data.user_id);
        toast.info('Please enter your two-factor authentication code');
        setLoading(false);
        return;
      }
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Also store the access token for mobile compatibility (Safari ITP blocks cookies)
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
      }
      
      // Track successful login
      identifyUser(response.data.user.id, {
        email: response.data.user.email,
        name: response.data.user.name,
        roles: response.data.user.roles,
      });
      trackEvent(EVENTS.USER_LOGGED_IN, {
        method: 'email',
        has_2fa: requires2FA,
      });
      
      // Initialize CSRF token
      await initializeCsrf();
      
      toast.success('Welcome back!');
      
      // Check if there's a return URL from before login
      const returnUrl = sessionStorage.getItem('returnUrl');
      if (returnUrl) {
        sessionStorage.removeItem('returnUrl');
        navigate(returnUrl);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      if (error.response?.data?.detail === 'Invalid 2FA code') {
        toast.error('Invalid verification code. Please try again.');
        setTwoFactorCode('');
      } else {
        toast.error(error.response?.data?.detail || 'Login failed. Please check your credentials.');
        // Reset 2FA state on other errors
        setRequires2FA(false);
        setTwoFactorCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFactorCode('');
    setPendingUserId(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 flex flex-col p-4 sm:p-6">
      <SkipNav />
      
      {/* Language Selector in top right */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="compact" />
      </div>
      
      <main id="main-content" className="flex-1 flex flex-col items-center justify-start w-full max-w-lg mx-auto px-4 pt-4 sm:pt-8">
        {/* Logo */}
        <header className="text-center mb-4 sm:mb-6 w-full">
          <div className="inline-flex items-center justify-center mb-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_role-expiry-dash/artifacts/swo6wssk_career_support_80px_transparent_EXACT.png" 
              alt="Career Support" 
              className="h-28 sm:h-36 w-auto"
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome Back</h1>
          <p className="text-sm sm:text-base text-gray-700">Log in to continue your career journey</p>
        </header>

        {/* Login Form */}
        <section className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 border border-gray-200 w-full">
          <form onSubmit={handleSubmit} className="space-y-6" aria-label="Login form">
            {/* Show 2FA input when required */}
            {requires2FA ? (
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center text-gray-600 hover:text-gray-900 mb-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </button>
                
                <div className="text-center mb-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <Smartphone className="h-8 w-8 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900">Two-Factor Authentication</h2>
                  <p className="text-sm text-gray-600 mt-2">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="2fa-code" className="text-sm font-medium mb-2 block text-center">
                    Verification Code
                  </Label>
                  <Input
                    id="2fa-code"
                    type="text"
                    placeholder="000000"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    maxLength={6}
                    className="h-14 text-center text-2xl tracking-[0.5em] font-mono"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 text-center mt-2">
                    You can also use a backup code
                  </p>
                </div>
                
                <Button
                  type="submit"
                  disabled={loading || twoFactorCode.length < 6}
                  className="w-full h-11 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white shadow-lg shadow-purple-500/30"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Verify & Log In'
                  )}
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                    {t('auth.email', 'Email Address')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      if (emailError) setEmailError('');
                    }}
                    required
                    aria-required="true"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "email-error" : undefined}
                    data-testid="login-email-input"
                    className={`h-11 ${emailError ? 'border-red-500' : ''}`}
                  />
                  {emailError && (
                    <p id="email-error" role="alert" className="text-red-600 text-sm mt-1">
                      {emailError}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm font-medium mb-2 block">
                    {t('auth.password', 'Password')}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t('auth.enterPassword', 'Enter your password')}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      aria-required="true"
                      data-testid="login-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                      aria-label={showPassword ? t('auth.hidePassword', 'Hide password') : t('auth.showPassword', 'Show password')}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Remember me and Forgot Password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="w-4 h-4 text-sky-600 bg-gray-100 border-gray-300 rounded focus:ring-sky-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-600">{t('auth.rememberMe', 'Remember me')}</span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-sm text-sky-600 hover:text-sky-700 font-medium"
                  >
                    {t('auth.forgotPassword', 'Forgot password?')}
                  </Link>
                </div>

                {/* Newsletter Subscription (Optional) */}
                <div className="pt-1">
                  <label className="flex items-start space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={subscribeNewsletter}
                      onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                      className="mt-0.5 w-4 h-4 text-sky-600 bg-gray-100 border-gray-300 rounded focus:ring-sky-500 focus:ring-2"
                      data-testid="login-newsletter-checkbox"
                    />
                    <span className="text-sm text-gray-600">
                      {t('auth.subscribeNewsletter', 'Subscribe to our bi-weekly newsletter for career tips')}{' '}
                      <span className="text-gray-400">({t('common.optional', 'Optional')})</span>
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-btn"
                  className="w-full h-11 bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-lg shadow-sky-500/30"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('auth.loggingIn', 'Logging in...')}
                    </>
                  ) : (
                    t('auth.login', 'Log In')
                  )}
                </Button>
              </>
            )}
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">{t('auth.orContinueWith', 'Or continue with')}</span>
            </div>
          </div>

          {/* LinkedIn Login */}
          <div className="space-y-3">
            <GoogleLoginButton 
              className="w-full h-11" 
              redirectPath="/dashboard"
              variant="outline"
            />
            <LinkedInLoginButton className="w-full h-11" />
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              {t('auth.noAccount', "Don't have an account?")}{' '}
              <Link to="/register" className="text-sky-600 font-semibold hover:text-sky-700">
                {t('auth.signUp', 'Sign up')}
              </Link>
            </p>
          </div>
        </section>

        {/* Back to home */}
        <nav className="mt-6 text-center" aria-label="Secondary navigation">
          <Link to="/" className="text-sm text-gray-700 hover:text-sky-600">
            {t('common.backToHome', '← Back to home')}
          </Link>
        </nav>
      </main>
    </div>
  );
};

export default Login;
